import React from 'react';
import lscache from 'lscache';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useCorpNavSections } from 'corp/lib/nav';
import { makeStyles } from 'design-system/styles';
import HideableNavSection from './HideableNavSection';
import SidebarLink from 'corp/components/UI/SidebarLink';

const SIDEBAR_CACHE_KEY = 'in_sidebar_we_trust';

const Sidebar: React.FC<{}> = () => {
  const classes = useStyles();

  const corpNavSections = useCorpNavSections();
  // Setting default to true to make the anti-sidebar crowd happy #im_a_good_person, Venmo me @craig-weiss-1
  const defualtSidebarState = lscache.get(SIDEBAR_CACHE_KEY) ?? true;
  const [collapsed, setCollapsed] = React.useState<boolean>(defualtSidebarState);

  const onToggleCollapsed = React.useCallback(() => {
    setCollapsed(value => !value);
    lscache.set(SIDEBAR_CACHE_KEY, !collapsed);
  }, [setCollapsed, collapsed]);

  return (
    <div className={classNames('nav', { collapsed })}>
      <span className="nav-icon" onClick={onToggleCollapsed}>
        <FontAwesomeIcon icon="caret-left" />
      </span>
      <div className="nav-section">
        {corpNavSections.map(section => {
          return (
            <HideableNavSection
              key={section.title}
              name={section.title}
              component={
                <div className="nav-section__list">
                  {section.links.map(link => {
                    return (
                      <SidebarLink
                        key={link.href}
                        href={link.href}
                        target={link.isExternal ? '_blank' : undefined}
                        rel={link.isExternal ? 'noopener noreferrer' : undefined}
                        dirLink={link.directoryLink}
                        classes={{ link: classes.link }}
                      >
                        {link.title}
                        {link.isExternal && ' '}
                        {link.isExternal && (
                          <FontAwesomeIcon icon="external-link-alt" style={{ marginLeft: '4px' }} />
                        )}
                      </SidebarLink>
                    );
                  })}
                </div>
              }
            />
          );
        })}
      </div>
      <style jsx>{`
        .nav {
          background: var(--color-RemoGray75);
          flex-shrink: 0;
          overflow: auto;
          padding: 16px 16px;
          font-size: 14px;
          line-height: 2;
          transition: width 0.5s ease-in-out;
          width: 280px;
          position: relative;
          margin-top: 60px;
          display: flex;
          flex-direction: column;
        }

        .nav.collapsed {
          width: 48px;
        }

        .nav.collapsed .nav-icon {
          transform: rotate(-180deg);
        }

        .nav.collapsed .nav-section {
          display: none;
        }

        .nav .nav-icon {
          position: absolute;
          top: 3px;
          right: 12px;
          cursor: pointer;
          padding: 10px;
          transition: transform 0.5s ease-in-out;
          color: white;
        }

        .nav-section {
          margin-bottom: 16px;
          white-space: nowrap;
          opacity: 1;
          transition: opacity 0.5s ease-in-out;
        }

        h4 {
          margin: 0 0 4px;
          color: white;
          font-size: 13px;
          text-transform: uppercase;
          font-weight: 500;
          color: var(--color-RemoGray20);
          letter-spacing: 0.5px;
        }

        em {
          opacity: 0.5;
        }
      `}</style>
    </div>
  );
};

Sidebar.displayName = 'Sidebar';

const useStyles = makeStyles({
  link: {
    color: 'white',
  },
});

export default Sidebar;
